import React, { useEffect, useRef, useState } from "react";
import { Upload, Eye, Download, RefreshCw, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { DOC_GROUPS, docUrl, fmtDate, isAdmin } from "../../lib/customers";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

const ACCEPT = ".jpg,.jpeg,.png,.pdf,.webp";
const CUSTOM = "__custom__";

const UploadForm = ({ cid, onDone }) => {
  const [category, setCategory] = useState("Personal Documents");
  const [docType, setDocType] = useState(DOC_GROUPS["Personal Documents"][0]);
  const [custom, setCustom] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const submit = async () => {
    const name = docType === CUSTOM ? custom.trim() : docType;
    if (!name || !file) { toast.error("Document type aur file chunein"); return; }
    const fd = new FormData();
    fd.append("category", docType === CUSTOM ? "Custom" : category); fd.append("doc_type", name); fd.append("name", name); fd.append("file", file);
    setBusy(true);
    try { await api.post(`/customers/${cid}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploaded"); setFile(null); setCustom(""); if (ref.current) ref.current.value = ""; onDone(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 grid grid-cols-1 sm:grid-cols-4 gap-3 text-sm" data-testid="doc-upload-form">
      <div>
        <Label className="text-xs">Category</Label>
        <select value={category} onChange={(e) => { setCategory(e.target.value); setDocType(DOC_GROUPS[e.target.value][0]); }} className="w-full h-9 rounded-md border border-input bg-white px-2" data-testid="doc-category">
          {Object.keys(DOC_GROUPS).map((g) => <option key={g}>{g}</option>)}
        </select>
      </div>
      <div>
        <Label className="text-xs">Document</Label>
        <select value={docType} onChange={(e) => setDocType(e.target.value)} className="w-full h-9 rounded-md border border-input bg-white px-2" data-testid="doc-type">
          {DOC_GROUPS[category].map((d) => <option key={d}>{d}</option>)}
          <option value={CUSTOM}>+ Add Custom Document</option>
        </select>
      </div>
      {docType === CUSTOM ? <div><Label className="text-xs">Custom naam</Label><Input value={custom} onChange={(e) => setCustom(e.target.value)} data-testid="doc-custom-name" /></div> : <div />}
      <div>
        <Label className="text-xs">File (JPG/PNG/PDF)</Label>
        <input ref={ref} type="file" accept={ACCEPT} onChange={(e) => setFile(e.target.files?.[0] || null)} className="block w-full text-xs mt-1.5" data-testid="doc-file" />
      </div>
      <div className="sm:col-span-4"><Button onClick={submit} disabled={busy} className="bg-teal-700 hover:bg-teal-800 text-white" data-testid="doc-upload-btn"><Upload className="w-4 h-4 mr-1" />{busy ? "Uploading…" : "Upload"}</Button></div>
    </div>
  );
};

const DocRow = ({ cid, d, onDone }) => {
  const ref = useRef(null);
  const replace = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try { await api.put(`/customers/${cid}/documents/${d.id}`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Replaced"); onDone(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
  };
  const del = async () => {
    if (!window.confirm(`${d.name} delete karein?`)) return;
    try { await api.delete(`/customers/${cid}/documents/${d.id}`); toast.success("Deleted"); onDone(); } catch { toast.error("Failed"); }
  };
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 py-2 border-t border-slate-100 text-sm" data-testid={`doc-row-${d.id}`}>
      <div className="flex items-center gap-2 min-w-0"><FileText className="w-4 h-4 text-teal-700 shrink-0" />
        <div className="min-w-0"><div className="font-medium truncate">{d.name}</div><div className="text-xs text-slate-500">{fmtDate(d.uploaded_at, true)} · {d.uploaded_by}</div></div>
      </div>
      <div className="flex gap-1">
        <a href={docUrl(cid, d.id)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-100" title="View" data-testid={`doc-view-${d.id}`}><Eye className="w-4 h-4" /></a>
        <a href={docUrl(cid, d.id, true)} className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-100" title="Download" data-testid={`doc-download-${d.id}`}><Download className="w-4 h-4" /></a>
        <button onClick={() => ref.current?.click()} className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-slate-100" title="Replace" data-testid={`doc-replace-${d.id}`}><RefreshCw className="w-4 h-4" /></button>
        <input ref={ref} type="file" accept={ACCEPT} className="hidden" onChange={replace} />
        {isAdmin() && <button onClick={del} className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-red-50 text-red-600" title="Delete" data-testid={`doc-delete-${d.id}`}><Trash2 className="w-4 h-4" /></button>}
      </div>
    </div>
  );
};

export const DocumentsTab = ({ c }) => {
  const [docs, setDocs] = useState([]);
  const load = () => api.get(`/customers/${c.id}/documents`).then((r) => setDocs(r.data || [])).catch(() => {});
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [c.id]);
  const groups = [...Object.keys(DOC_GROUPS), "Custom"].map((g) => [g, docs.filter((d) => d.category === g)]).filter(([, l]) => l.length);
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 mt-3 space-y-4" data-testid="documents-tab">
      {!c.consent && <div className="rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900" data-testid="doc-consent-warning">Documents save karne se pehle customer ki sahmati (Consent) zaroori hai — Edit me "Consent" tick karein.</div>}
      {c.consent && <UploadForm cid={c.id} onDone={load} />}
      {groups.length === 0 && <div className="text-sm text-slate-500">Abhi koi document upload nahi hai.</div>}
      {groups.map(([g, list]) => (
        <div key={g}><div className="text-[11px] uppercase tracking-widest font-semibold text-teal-700 mb-1">{g}</div>
          {list.map((d) => <DocRow key={d.id} cid={c.id} d={d} onDone={load} />)}
        </div>
      ))}
      <div className="text-xs text-slate-400">Files private storage me surakshit hain — public link nahi banta.</div>
    </div>
  );
};
